/**
 * BL-194 Gate 1 — Module 5 Dashboard pure helpers.
 *
 * Shared by the CRA client bundle and the dashboard-threshold-check Edge
 * Function. Keep this file free of any browser client or src/config imports so
 * Deno can bundle it directly.
 */

/** Metric keys the KPI tile can be pointed at, and how each is read/formatted. */
export const KPI_METRICS = {
    revenue: {
        label: 'Revenue (30d)',
        field: 'revenue_collected_30d',
        priorField: 'revenue_collected_prior_30d',
        format: 'currency',
        trendDirection: 'up',
    },
    revenue_invoiced: {
        label: 'Invoiced (30d)',
        field: 'revenue_invoiced_30d',
        format: 'currency',
        trendDirection: 'up',
    },
    outstanding: {
        label: 'Outstanding Balance',
        field: 'outstanding_balance',
        format: 'currency',
        trendDirection: 'down',
    },
    overdue_invoices: {
        label: 'Overdue Invoices',
        field: 'overdue_invoice_count',
        format: 'number',
        trendDirection: 'down',
    },
    engagements: {
        label: 'Bookings (30d)',
        field: 'engagements_30d',
        format: 'number',
        trendDirection: 'up',
    },
    engagements_completed: {
        label: 'Completed (30d)',
        field: 'engagements_completed_30d',
        format: 'number',
        trendDirection: 'up',
    },
    open_engagements: {
        label: 'Open Bookings',
        field: 'engagements_open',
        format: 'number',
        trendDirection: 'up',
    },
    active_clients: {
        label: 'Active Clients',
        field: 'active_accounts',
        format: 'number',
        trendDirection: 'up',
    },
    new_clients: {
        label: 'New Clients (30d)',
        field: 'new_accounts_30d',
        format: 'number',
        trendDirection: 'up',
    },
};

/** How many days of history each timeframe token asks for. */
export const TIMEFRAME_DAYS = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '365d': 365,
};

export function timeframeDays(timeframe) {
    return TIMEFRAME_DAYS[timeframe] || TIMEFRAME_DAYS['30d'];
}

export function hasDashboardSourceError(metrics, source) {
    return Boolean(metrics?.errors?.some((error) => error.source === source));
}

/** The first day of the requested window, as a YYYY-MM-DD string. */
export function windowStartDate(timeframe, today = new Date()) {
    const start = new Date(today.getTime());
    start.setDate(start.getDate() - (timeframeDays(timeframe) - 1));
    return start.toISOString().slice(0, 10);
}

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Fill in the days that have no rows so a chart reads as a continuous
 * timeline rather than skipping quiet days.
 *
 * @param {Array} rows — dashboard_revenue_trend rows (bucket_date ascending or not)
 * @param {string} timeframe
 * @param {Date} today
 * @returns {Array<{date: string, label: string, revenue: number, invoiced: number, comparison: number}>}
 */
export function buildRevenueSeries(rows, timeframe = '30d', today = new Date()) {
    const byDate = new Map();
    for (const row of rows || []) {
        if (!row?.bucket_date) continue;
        const key = String(row.bucket_date).slice(0, 10);
        byDate.set(key, row);
    }

    const days = timeframeDays(timeframe);
    const series = [];
    for (let offset = days - 1; offset >= 0; offset -= 1) {
        const day = new Date(today.getTime());
        day.setDate(day.getDate() - offset);
        const key = day.toISOString().slice(0, 10);
        const row = byDate.get(key);
        series.push({
            date: key,
            label: day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            revenue: toNumber(row?.collected_amount),
            invoiced: toNumber(row?.invoiced_amount),
            comparison: toNumber(row?.invoiced_amount),
        });
    }
    return series;
}

/**
 * Resolve one KPI tile's value and period-over-period trend from the rollup row.
 *
 * A metric with no prior-period field (or a zero baseline) reports no trend at
 * all rather than a fabricated 0% or an infinite jump.
 */
export function resolveKpiMetric(rollup, metricKey) {
    const definition = KPI_METRICS[metricKey] || KPI_METRICS.revenue;
    const rawValue = rollup?.[definition.field];
    const value = toNumber(rawValue);

    let trend = 0;
    let hasTrend = false;
    if (definition.priorField && rollup) {
        const prior = toNumber(rollup[definition.priorField]);
        if (prior !== 0) {
            trend = Math.round(((value - prior) / prior) * 1000) / 10;
            hasTrend = true;
        }
    }

    return {
        metricKey,
        label: definition.label,
        value,
        available: rawValue !== null && rawValue !== undefined,
        format: definition.format,
        trendDirection: definition.trendDirection,
        trend,
        hasTrend,
    };
}

/**
 * Turn the 7x24 dead-zone grid into the quietest windows, worst first.
 *
 * Slots outside operating hours are noise on a dashboard, so callers pass the
 * hours they actually trade in; the default is the whole day.
 */
export function summarizeDeadZones(rows, { limit = 5, fromHour = 0, toHour = 23 } = {}) {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (rows || [])
        .filter((row) => row && row.hour_of_day >= fromHour && row.hour_of_day <= toHour)
        .map((row) => ({
            dayOfWeek: row.day_of_week,
            hourOfDay: row.hour_of_day,
            label: `${dayNames[row.day_of_week] || '?'} ${String(row.hour_of_day).padStart(2, '0')}:00`,
            engagementCount: toNumber(row.engagement_count),
            engagementAmount: toNumber(row.engagement_amount),
            isDeadZone: Boolean(row.is_dead_zone),
        }))
        .sort((a, b) => (
            a.engagementCount - b.engagementCount
            || a.dayOfWeek - b.dayOfWeek
            || a.hourOfDay - b.hourOfDay
        ))
        .slice(0, limit);
}

/**
 * Shape a dashboard_recent_activity row for the activity tile.
 * Icons/colours are chosen here so every consumer of the feed agrees.
 */
const ACTIVITY_PRESENTATION = {
    invoice: { icon: '📄', color: 'blue' },
    payment: { icon: '💰', color: 'green' },
    engagement: { icon: '📅', color: 'orange' },
    account: { icon: '👤', color: 'purple' },
};

export function shapeActivityFeed(rows, maxItems = 10, activityTypes = ['all']) {
    const wanted = new Set(activityTypes || ['all']);
    const includeAll = wanted.has('all') || wanted.size === 0;

    return (rows || [])
        .filter((row) => row && (includeAll || wanted.has(row.activity_type)))
        .map((row) => {
            const presentation = ACTIVITY_PRESENTATION[row.activity_type] || { icon: '🔔', color: 'blue' };
            const amount = toNumber(row.amount);
            return {
                id: `${row.activity_type}:${row.source_id}`,
                type: row.activity_type,
                icon: presentation.icon,
                color: presentation.color,
                title: row.title || 'Activity',
                description: amount
                    ? `${amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}${row.status ? ` · ${row.status}` : ''}`
                    : (row.status || ''),
                timestamp: row.occurred_at ? new Date(row.occurred_at) : null,
            };
        })
        .slice(0, maxItems);
}

/**
 * Evaluate one threshold row against a measured value.
 *
 * Shared with the dashboard-threshold-check Edge Function so the client preview
 * of "what would fire" and the scheduled check can never drift apart. Gate 3
 * owns what happens to a breach; this only decides whether there is one.
 */
export function evaluateThreshold(threshold, measuredValue) {
    if (!threshold || threshold.is_active === false) return null;
    if (measuredValue === null || measuredValue === undefined) return null;

    const value = Number(measuredValue);
    const limit = Number(threshold.threshold_value);
    if (!Number.isFinite(value) || !Number.isFinite(limit)) return null;

    let breached;
    switch (threshold.comparator) {
        case 'lt': breached = value < limit; break;
        case 'lte': breached = value <= limit; break;
        case 'gt': breached = value > limit; break;
        case 'gte': breached = value >= limit; break;
        default: return null;
    }

    if (!breached) return null;

    return {
        thresholdId: threshold.id,
        orgId: threshold.org_id,
        metricKey: threshold.metric_key,
        comparator: threshold.comparator,
        thresholdValue: limit,
        measuredValue: value,
        severity: threshold.severity || 'info',
    };
}

/**
 * Map a threshold metric_key onto the measured value for one org.
 *
 * Kept here (rather than inside the Edge Function) so the mapping is unit
 * tested and stays the single definition of what each watched metric means.
 */
export function measureThresholdMetric(metricKey, { rollup, velocity, goalGap } = {}) {
    switch (metricKey) {
        case 'revenue_collected_30d': return toNumber(rollup?.revenue_collected_30d);
        case 'revenue_invoiced_30d': return toNumber(rollup?.revenue_invoiced_30d);
        case 'outstanding_balance': return toNumber(rollup?.outstanding_balance);
        case 'overdue_invoice_count': return toNumber(rollup?.overdue_invoice_count);
        case 'engagements_completed_30d': return toNumber(rollup?.engagements_completed_30d);
        case 'velocity_pct_change': {
            const pct = velocity?.revenue_pct_change;
            return pct === null || pct === undefined ? null : Number(pct);
        }
        case 'goal_gap_pct': {
            // The worst-attained active goal is the one worth alerting on.
            const attainments = (goalGap || [])
                .map((goal) => Number(goal?.attainment_pct))
                .filter((pct) => Number.isFinite(pct));
            return attainments.length ? Math.min(...attainments) : null;
        }
        default: return null;
    }
}
